import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  IconButton,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Sidebar from "../Sidebar";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const years = Array.from({ length: 2040 - 1980 + 1 }, (_, i) => 1980 + i);

const EducationSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [entries, setEntries] = useState([]);
  const [educationLevel, setEducationLevel] = useState("school");
  
  const [form, setForm] = useState({
    educationLevel: "school",

    // School fields
    standard: "",
    schoolName: "",
    schoolLocation: "",
    district: "",
    stream: "",
    percentage: "",

    // College fields
    programType: "",
    collegeName: "",
    collegeLocation: "",
    degreeName: "",
    degreeType: "",
    specialization: "",
    cgpa: "",
    grade: "",
    gradMonth: "",
    gradYear: "",
    additionalCoursework: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Persist template/theme across navigation
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    const navTemplate = location.state?.selectedTemplate;
    if (navTemplate) {
      localStorage.setItem("selectedTemplate", navTemplate);
      return navTemplate;
    }
    return localStorage.getItem("selectedTemplate") || 1;
  });
  const [selectedTheme, setSelectedTheme] = useState(() => {
    const navTheme = location.state?.selectedTheme;
    const parseTheme = (t) => {
      if (!t) return null;
      if (typeof t === 'string') return t;
      if (typeof t === 'object') return t.primary || t.color || t.highlight || t.primary?.main || t.hex || null;
      return null;
    };

    const fromNav = parseTheme(navTheme);
    if (fromNav) {
      localStorage.setItem('selectedTheme', fromNav);
      return fromNav;
    }

    const stored = localStorage.getItem('selectedTheme');
    if (!stored) return '#1a2b47';
    try {
      const parsed = JSON.parse(stored);
      const fromParsed = parseTheme(parsed);
      return fromParsed || stored || '#1a2b47';
    } catch {
      return stored;
    }
  });

  // If navigation state changes and has new template/theme, update state and localStorage
  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) {
        setSelectedTemplate(template);
        localStorage.setItem("selectedTemplate", template);
      }
      if (theme) {
        const normalized = typeof theme === 'string' ? theme : (theme.primary || theme.color || theme.highlight || theme.primary?.main || theme.hex || null);
        if (normalized) {
          setSelectedTheme(normalized);
          localStorage.setItem('selectedTheme', normalized);
        }
      }
    }
  }, [location.state]);

  // Fetch existing entries from localStorage
  useEffect(() => {
    fetchEducationEntries();
  }, []);

  const fetchEducationEntries = () => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) {
      setEntries(JSON.parse(storedEntries));
    }
  };

  const saveEducationEntries = (entries) => {
    localStorage.setItem("educationEntries", JSON.stringify(entries));
    setEntries(entries);
  };
const handleChange = (e) => {
  const { name, value } = e.target;
  
  if (name === "educationLevel") {
    setEducationLevel(value);
    setForm({
      educationLevel: value,
      standard: "",
      schoolName: "",
      schoolLocation: "",
      district: "",
      stream: "",
      percentage: "",
      programType: "",
      collegeName: "",
      collegeLocation: "",
      degreeName: "",
      degreeType: "",
      specialization: "",
      cgpa: "",
      grade: "",
      gradMonth: "",
      gradYear: "",
      additionalCoursework: "",
    });
  } else if (name === "cgpa") {
    // Auto-calculate percentage from CGPA using Anna University formula: (CGPA - 0.75) x 10
    const cgpaValue = parseFloat(value);
    const calculatedPercentage =
      !isNaN(cgpaValue) && cgpaValue >= 0 && cgpaValue <= 10
        ? ((cgpaValue - 0.75) * 10).toFixed(2)
        : "";
    setForm({ ...form, cgpa: value, percentage: calculatedPercentage });
  } else {
    setForm({ ...form, [name]: value });
  }
};

  const validateForm = () => {
    if (educationLevel === "school") {
      return form.schoolName && form.schoolLocation && form.standard;
    } else {
      return form.collegeName && form.collegeLocation && form.degreeName;
    }
  };

  const handleAdd = () => {
    if (!validateForm()) return;
    
    const newEntry = {
      id: Date.now(),
      ...form,
      educationLevel
    };
    
    const updatedEntries = [newEntry, ...entries];
    saveEducationEntries(updatedEntries);
    resetForm();
  };

  const handleEdit = (edu) => {
    setEducationLevel(edu.educationLevel);
    setForm({
      educationLevel: edu.educationLevel,
      standard: edu.standard || "",
      schoolName: edu.schoolName || "",
      schoolLocation: edu.schoolLocation || "",
      district: edu.district || "",
      stream: edu.stream || "",
      percentage: edu.percentage || "",
      programType: edu.programType || "",
      collegeName: edu.collegeName || "",
      collegeLocation: edu.collegeLocation || "",
      degreeName: edu.degreeName || "",
      degreeType: edu.degreeType || "",
      specialization: edu.specialization || "",
      cgpa: edu.cgpa || "",
      grade: edu.grade || "",
      gradMonth: edu.gradMonth || "",
      gradYear: edu.gradYear || "",
      additionalCoursework: edu.additionalCoursework || "",
    });
    setEditMode(true);
    setCurrentEditId(edu.id);
    
    // Scroll to form on mobile when editing
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleUpdate = () => {
    if (!validateForm()) return;
    
    const updatedEntries = entries.map(entry => 
      entry.id === currentEditId 
        ? { id: currentEditId, ...form, educationLevel }
        : entry
    );
    
    saveEducationEntries(updatedEntries);
    resetForm();
  };

  const handleDeleteClick = (id) => {
    setEntryToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    const updatedEntries = entries.filter(e => e.id !== entryToDelete);
    saveEducationEntries(updatedEntries);
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  };

  const resetForm = () => {
    setEducationLevel("school");
    setForm({
      educationLevel: "school",
      standard: "",
      schoolName: "",
      schoolLocation: "",
      district: "",
      stream: "",
      percentage: "",
      programType: "",
      collegeName: "",
      collegeLocation: "",
      degreeName: "",
      degreeType: "",
      specialization: "",
      cgpa: "",
      grade: "",
      gradMonth: "",
      gradYear: "",
      additionalCoursework: "",
    });
    setEditMode(false);
    setCurrentEditId(null);
  };

  const getEducationDisplayText = (entry) => {
    if (entry.educationLevel === "school") {
      return `${entry.standard} • ${entry.schoolName}`;
    } else {
      return `${entry.degreeName} • ${entry.collegeName}`;
    }
  };

  const getEducationDetails = (entry) => {
    if (entry.educationLevel === "school") {
      return `📍 ${entry.schoolLocation} • 📊 ${entry.percentage}% • 🏛️ ${entry.district}`;
    } else {
      return `📍 ${entry.collegeLocation} • 🎓 ${entry.specialization || entry.degreeType}`;
    }
  };

  // Handle Go Back navigation to EducationTips
  const handleGoBack = () => {
    navigate("/EducationTips", {
      state: { selectedTemplate, selectedTheme },
    });
  };

  const renderEducationLevelRadio = () => (
    <Card sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: 2, border: "1px solid #B3D9F7", borderLeft: "4px solid #002B5B" }}>
      <CardContent sx={{ p: isSmallMobile ? 1 : 2 }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ 
            fontSize: isSmallMobile ? "1rem" : "1.1rem",
            fontWeight: "bold",
            color: "#002B5B"
          }}
        >
          🎯 Select Education Level
        </Typography>
        <RadioGroup
          row
          value={educationLevel}
          onChange={handleChange}
          name="educationLevel"
          sx={{
            flexDirection: isSmallMobile ? "column" : "row",
            gap: isSmallMobile ? 1 : 3,
            mt: 1
          }}
        >
          <FormControlLabel 
            value="school" 
            control={
              <Radio 
                sx={{
                  color: "#B3D9F7",
                  "&.Mui-checked": { color: "#002B5B" },
                }}
              />
            } 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '1.2em' }}>🏫</span>
                <Typography sx={{ fontSize: isSmallMobile ? '0.9rem' : 'inherit' }}>
                  School
                </Typography>
              </Box>
            } 
          />
          <FormControlLabel 
            value="college" 
            control={
              <Radio 
                sx={{
                  color: "#B3D9F7",
                  "&.Mui-checked": { color: "#002B5B" },
                }}
              />
            } 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '1.2em' }}>🎓</span>
                <Typography sx={{ fontSize: isSmallMobile ? '0.9rem' : 'inherit' }}>
                  College / University
                </Typography>
              </Box>
            } 
          />
        </RadioGroup>
      </CardContent>
    </Card>
  );

  const renderSchoolForm = () => (
    <Card sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: 2, border: "1px solid #B3D9F7", borderLeft: "4px solid #002B5B" }}>
      <CardContent sx={{ p: isSmallMobile ? 1 : 2 }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ 
            fontSize: isSmallMobile ? "1rem" : "1.1rem",
            fontWeight: "bold",
            color: "#002B5B"
          }}
        >
          🏫 School Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Standard"
              name="standard"
              value={form.standard}
              placeholder="Select standard (e.g., 10th, 12th)"
              onChange={handleChange}
              required
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "220px",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            >
              <MenuItem value="10th">10th Standard</MenuItem>
              <MenuItem value="11th">11th Standard</MenuItem>
              <MenuItem value="12th">12th Standard</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="School Name"
              name="schoolName"
              value={form.schoolName}
              placeholder="Enter school name"
              onChange={handleChange}
              required
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="School Location"
              name="schoolLocation"
              value={form.schoolLocation}
              placeholder="City, State (e.g., Mumbai, MH)"
              onChange={handleChange}
              required
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="District"
              name="district"
              value={form.district}
              placeholder="Enter district (optional)"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Stream"
              name="stream"
              value={form.stream}
              placeholder="Stream (e.g., Science, Commerce)"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Percentage (%)"
              name="percentage"
              value={form.percentage}
              placeholder="Percentage (e.g., 88.5)"
              onChange={handleChange}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "220px",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderCollegeForm = () => (
    <Card sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: 2, border: "1px solid #B3D9F7", borderLeft: "4px solid #002B5B" }}>
      <CardContent sx={{ p: isSmallMobile ? 1 : 2 }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ 
            fontSize: isSmallMobile ? "1rem" : "1.1rem",
            fontWeight: "bold",
            color: "#002B5B"
          }}
        >
          🎓 College / University Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Program Type"
              name="programType"
              value={form.programType}
              placeholder="Select program type (UG/PG)"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "220px",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            >
              <MenuItem value="UG">Undergraduate (UG)</MenuItem>
              <MenuItem value="PG">Postgraduate (PG)</MenuItem>
              <MenuItem value="Diploma">Diploma</MenuItem>
              <MenuItem value="Certificate">Certificate</MenuItem>
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="College/University Name"
              name="collegeName"
              value={form.collegeName}
              placeholder="Enter college or university name"
              onChange={handleChange}
              required
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="College Location"
              name="collegeLocation"
              value={form.collegeLocation}
              placeholder="City, State (e.g., Pune, MH)"
              onChange={handleChange}
              required
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Degree Name"
              name="degreeName"
              value={form.degreeName}
              placeholder="Degree name (e.g., B.Sc Computer Science)"
              onChange={handleChange}
              required
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Degree Type"
              name="degreeType"
              value={form.degreeType}
              placeholder="Degree type (e.g., Honours, Pass)"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Specialization"
              name="specialization"
              value={form.specialization}
              placeholder="Specialization or major (e.g., AI)"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="CGPA"
              name="cgpa"
              value={form.cgpa}
              placeholder="CGPA (e.g., 8.5)"
              onChange={handleChange}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 10, step: 0.01 } }}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "150px",
                  md: "200px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
        <Grid item xs={12} sm={4}>
  <TextField
    fullWidth
    label="Percentage (%)"
    name="percentage"
    value={form.percentage}
    InputProps={{ 
      readOnly: true,
      inputProps: { min: 0, max: 100, step: 0.1 }
    }}
    size={isSmallMobile ? "small" : "medium"}
    sx={{
      width: {
        xs: "100%",
        sm: "150px",
        md: "200px",
        lg: "300px",
      },
      "& .MuiInputBase-input": {
        backgroundColor: "#EFF7FF",
        cursor: "not-allowed",
        color: "#0D4C8B",
      },
    }}
    helperText="Auto-calculated from CGPA"
  />
</Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Grade"
              name="grade"
              value={form.grade}
              placeholder="Grade (e.g., A, First Class)"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "100%",
                  sm: "150px",
                  md: "200px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Graduation Month"
              name="gradMonth"
              value={form.gradMonth}
              placeholder="Select graduation month"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "220px",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            >
              {months.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Graduation Year"
              name="gradYear"
              value={form.gradYear}
              placeholder="Select graduation year"
              onChange={handleChange}
              size={isSmallMobile ? "small" : "medium"}
              sx={{
                width: {
                  xs: "220px",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={isSmallMobile ? 2 : 3}
              label="Additional Coursework"
              name="additionalCoursework"
              value={form.additionalCoursework}
              onChange={handleChange}
              inputProps={{ maxLength: 70 }}
               sx={{
                width: {
                  xs: "220px",
                  sm: "200px",
                  md: "250px",
                  lg: "300px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#B3D9F7" },
                  "&:hover fieldset": { borderColor: "#0288D1" },
                  "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                },
              }}
              helperText={`${(form.additionalCoursework || "").length}/70 characters`}
              size={isSmallMobile ? "small" : "medium"}
              placeholder="List any relevant coursework, projects, or achievements..."
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />

      {/* Main content area — pushed right of sidebar (same as WorkHistrySummary) */}
      <Box
        sx={{
          ml: { xs: "60px", sm: "240px" },
          width: { xs: "calc(100% - 60px)", sm: "calc(100% - 240px)" },
          minHeight: "100vh",
          p: { xs: 1.5, sm: 3 },
          boxSizing: "border-box",
          backgroundColor: "#EFF7FF",
        }}
      >
        {/* Go Back Button */}
        <Typography
          variant="body2"
          sx={{
            color: "#002B5B",
            fontWeight: "bold",
            mb: 2,
            cursor: "pointer",
            "&:hover": {
              color: "#0288D1",
              textDecoration: "underline",
            },
            transition: "color 0.3s ease",
            fontSize: isSmallMobile ? "0.875rem" : "inherit",
          }}
          onClick={handleGoBack}
        >
          ← Go Back
        </Typography>

        {/* Header Section */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          mb: 3,
          flexDirection: isSmallMobile ? "column" : "row",
          gap: isSmallMobile ? 2 : 0
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold"
            sx={{ 
              fontSize: isSmallMobile ? "1.5rem" : "inherit",
              textAlign: isSmallMobile ? "center" : "left",
              color: "#002B5B"
            }}
          >
            Education Summary
          </Typography>
          
          {entries.length > 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: isSmallMobile ? "0.8rem" : "inherit",
                textAlign: isSmallMobile ? "center" : "left",
                color: "#0288D1"
              }}
            >
              {entries.length} education {entries.length === 1 ? 'entry' : 'entries'}
            </Typography>
          )}
        </Box>

        {/* Education Form */}
        <Card sx={{ 
          mb: 4, 
          p: isSmallMobile ? 2 : 3,
          boxShadow: 2,
          borderRadius: 2,
          border: "1px solid #B3D9F7",
          borderLeft: "4px solid #002B5B"
        }}>
          <CardContent sx={{ p: isSmallMobile ? 1 : 2 }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom
              sx={{ 
                fontSize: isSmallMobile ? "1.1rem" : "inherit",
                mb: 3,
                color: "#002B5B"
              }}
            >
              {editMode ? "✏️ Edit Education" : "➕ Add New Education"}
            </Typography>
            
            {renderEducationLevelRadio()}
            {educationLevel === "school" ? renderSchoolForm() : renderCollegeForm()}
            
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              flexDirection: isSmallMobile ? "column" : "row",
              mt: 3
            }}>
              <Button 
                onClick={editMode ? handleUpdate : handleAdd} 
                variant="contained"
                disabled={!validateForm()}
                sx={{ 
                  width: isSmallMobile ? "100%" : "auto",
                  minWidth: isSmallMobile ? "100%" : "200px",
                  backgroundColor: "#002B5B",
                  "&:hover": { backgroundColor: "#0288D1" },
                }}
              >
                {editMode ? "Update Education" : "Add Education"}
              </Button>
              {editMode && (
                <Button 
                  onClick={resetForm} 
                  variant="outlined"
                  sx={{ 
                    width: isSmallMobile ? "100%" : "auto",
                    minWidth: isSmallMobile ? "100%" : "120px",
                    borderColor: "#002B5B",
                    color: "#002B5B",
                    "&:hover": {
                      borderColor: "#0288D1",
                      color: "#0288D1",
                      backgroundColor: "rgba(2, 136, 209, 0.04)",
                    },
                  }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Education Entries List */}
        <Box sx={{ mt: 4 }}>
          {entries.length === 0 ? (
            <Paper sx={{ 
              p: isSmallMobile ? 3 : 4, 
              textAlign: 'center',
              borderRadius: 2,
              boxShadow: 1,
              border: "1px solid #B3D9F7"
            }}>
              <Typography 
                variant="h6" 
                sx={{ fontSize: isSmallMobile ? "1rem" : "inherit", color: "#0288D1" }}
              >
                🎓 No education entries yet. Add your first education above.
              </Typography>
            </Paper>
          ) : (
            entries.map((edu) => (
              <Paper 
                key={edu.id} 
                sx={{ 
                  p: isSmallMobile ? 2 : 3, 
                  mb: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  flexDirection: isSmallMobile ? "column" : "row",
                  gap: isSmallMobile ? 2 : 1,
                  borderRadius: 2,
                  boxShadow: 2,
                  position: 'relative',
                  border: "1px solid #B3D9F7",
                  borderLeft: "4px solid #002B5B",
                  '&:hover': {
                    boxShadow: 3,
                  }
                }}
              >
                <Box sx={{ 
                  width: isSmallMobile ? "100%" : "100%",
                  pr: isSmallMobile ? 0 : 2 
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    mb: 1 
                  }}>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        backgroundColor: edu.educationLevel === "school" ? "#002B5B" : "#0288D1",
                        color: "white",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: isSmallMobile ? "0.7rem" : "0.8rem"
                      }}
                    >
                      {edu.educationLevel === "school" ? "🏫 School" : "🎓 College"}
                    </Typography>
                    {edu.gradMonth && edu.gradYear && (
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: isSmallMobile ? "0.7rem" : "0.8rem", color: "#0D4C8B" }}
                      >
                        🗓️ {edu.gradMonth} {edu.gradYear}
                      </Typography>
                    )}
                  </Box>
                  
                  <Typography 
                    fontWeight="bold"
                    sx={{ 
                      fontSize: isSmallMobile ? "1rem" : "1.1rem",
                      color: "#002B5B",
                      mb: 0.5
                    }}
                  >
                    {getEducationDisplayText(edu)}
                  </Typography>
                  
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: isSmallMobile ? "0.8rem" : "inherit", mb: 1, color: "#0D4C8B" }}
                  >
                    {getEducationDetails(edu)}
                  </Typography>
                  
                  {edu.stream && (
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: isSmallMobile ? "0.8rem" : "inherit", mb: 0.5 }}
                    >
                      <strong>Stream:</strong> {edu.stream}
                    </Typography>
                  )}
                  
                  {edu.specialization && (
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: isSmallMobile ? "0.8rem" : "inherit", mb: 0.5 }}
                    >
                      <strong>Specialization:</strong> {edu.specialization}
                    </Typography>
                  )}
                  
                  {(edu.cgpa || edu.percentage || edu.grade) && (
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: isSmallMobile ? "0.8rem" : "inherit", mb: 0.5 }}
                    >
                      <strong>Performance:</strong> 
                      {edu.cgpa && ` CGPA: ${edu.cgpa}`}
                      {edu.percentage && ` Percentage: ${edu.percentage}%`}
                      {edu.grade && ` Grade: ${edu.grade}`}
                    </Typography>
                  )}
                  
                  {edu.additionalCoursework && (
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: isSmallMobile ? "0.8rem" : "inherit",
                        mt: 1,
                        p: 1,
                        backgroundColor: "#EFF7FF",
                        borderRadius: 1
                      }}
                    >
                      <strong>Additional Details:</strong> {edu.additionalCoursework}
                    </Typography>
                  )}
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex',
                    justifyContent: isSmallMobile ? "space-between" : "flex-start",
                    width: isSmallMobile ? "100%" : "auto",
                    gap: isSmallMobile ? 1 : 0.5
                  }}
                >
                  <IconButton 
                    onClick={() => handleEdit(edu)} 
                    size={isSmallMobile ? "medium" : "medium"}
                    sx={{ 
                      backgroundColor: "#0288D1", 
                      color: "white",
                      '&:hover': { backgroundColor: "#002B5B" }
                    }}
                  >
                    <EditIcon fontSize={isSmallMobile ? "small" : "medium"} />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDeleteClick(edu.id)} 
                    size={isSmallMobile ? "medium" : "medium"}
                    sx={{ 
                      backgroundColor: "error.light", 
                      color: "white",
                      '&:hover': { backgroundColor: "error.main" }
                    }}
                  >
                    <DeleteIcon fontSize={isSmallMobile ? "small" : "medium"} />
                  </IconButton>
                </Box>
              </Paper>
            ))
          )}
        </Box>

        {/* Next Button */}
        {entries.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: isSmallMobile ? "center" : "flex-end", 
            mt: 4,
            pt: 3,
            borderTop: "1px solid",
            borderColor: "divider"
          }}>
            <Button
              variant="contained"
              size={isSmallMobile ? "large" : "medium"}
              sx={{ 
                backgroundColor: "#002B5B", 
                "&:hover": { backgroundColor: "#0288D1" },
                width: isSmallMobile ? "100%" : "auto",
                minWidth: isSmallMobile ? "100%" : "200px",
                py: isSmallMobile ? 1.5 : 1
              }}
              onClick={() => navigate("/skills", { 
                state: { 
                  selectedTemplate, 
                  selectedTheme 
                } 
              })}
            >
              Next: Skills →
            </Button>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Delete Education Entry?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this education entry? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} sx={{ color: "#002B5B" }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default EducationSection;